/**
 * Small pure helpers for the AI generator UI: the progress stage list, the
 * download file name and the "is this an icon?" prompt check.
 *
 * Plain dependency-free JS (no React, no DOM) so it can be unit tested with
 * plain `node` — see app/ai-generator/__tests__/generatorUi.test.js.
 */

// ── Progress stages ─────────────────────────────────────────────────────────
// The backend reports coarse job statuses (routes/ai-generator.js,
// updateJobStatus → progressMessages), never a percentage. Instead of a
// synthetic percent, the panel shows which of three stages the job is in.
// Every status the pipeline emits while working maps to one of them; the
// concept-pipeline statuses (sketching, transferring…) are all "Drawing".
export const PROGRESS_STAGES = Object.freeze([
  {
    key: 'drawing',
    label: 'Drawing',
    statuses: ['queued', 'pending', 'planning', 'generating', 'concepting', 'concepts_ready',
      'revising', 'auto_picking', 'transferring'],
  },
  { key: 'validating', label: 'Validating', statuses: ['validating', 'fixing'] },
  { key: 'rendering', label: 'Rendering preview', statuses: ['rendering', 'reviewing'] },
]);

const DONE_STATUSES = new Set(['completed', 'variants_ready', 'awaiting_pick']);

/**
 * Index of the stage a job status belongs to: 0…2, 3 once the job is done,
 * 0 for anything unknown (a new status still reads as "working on it").
 */
export function stageIndexOf(status) {
  if (DONE_STATUSES.has(status)) return PROGRESS_STAGES.length;
  const i = PROGRESS_STAGES.findIndex((st) => st.statuses.includes(status));
  return i < 0 ? 0 : i;
}

// ── Download file name ──────────────────────────────────────────────────────
const MAX_NAME_CHARS = 40;
const MAX_NAME_WORDS = 4;

// Request boilerplate in front of the thing itself: "Create a …", "I need an …".
const LEAD_RE = /^(?:(?:please|pls|can you|could you|i need|i want|we need|give me|make me)\s+)?(?:create|make|draw|design|generate|build|render|produce|add)?\s*(?:me\s+)?(?:a|an|the|one)?\s+/i;
// Words that end the name: everything after them describes the widget.
const STOP_WORDS = new Set([
  'widget', 'widgets', 'for', 'with', 'that', 'which', 'showing', 'shows', 'in', 'on', 'of',
  'to', 'and', 'where', 'using', 'featuring', 'including', 'like', 'from', 'by',
]);
const FILLER_WORDS = new Set(['a', 'an', 'the', 'high-fidelity', 'simple', 'compact', 'modern', 'new']);

const asciiWords = (text) => String(text || '')
  .normalize('NFKD')
  .replace(/[̀-ͯ]/g, '')
  .replace(/[^\x20-\x7e]/g, ' ')
  .split(/[^A-Za-z0-9-]+/)
  .map((w) => w.replace(/^-+|-+$/g, ''))
  .filter(Boolean);

const camel = (w) => w
  .split('-')
  .filter(Boolean)
  .map((part) => (part.length > 1 && part === part.toUpperCase()
    ? part // keep acronyms: LED, ANSI, PT-101 → PT101
    : part.charAt(0).toUpperCase() + part.slice(1).toLowerCase()))
  .join('');

/**
 * File name for a downloaded widget, from its prompt: "Create a pressure
 * gauge widget for…" → "PressureGauge.svghmi". ASCII, CamelCase, at most 40
 * characters before the extension; "ai-widget-<first 8 of id>.svghmi" when the
 * prompt yields nothing usable (empty, non-Latin script…).
 *
 * @param {string} prompt
 * @param {string} [jobId]
 */
export function widgetFileName(prompt, jobId) {
  // The name lives in the first clause: cut at sentence ends, dashes, commas…
  const firstSentence = String(prompt || '').split(/[.!?\n:;,(/—–]|\s-\s/)[0].trim();
  const stripped = firstSentence.replace(LEAD_RE, '');
  const words = [];
  asciiWords(stripped).some((w) => {
    const lower = w.toLowerCase();
    if (STOP_WORDS.has(lower)) return words.length > 0;
    if (FILLER_WORDS.has(lower) && words.length === 0) return false;
    words.push(w);
    return words.length >= MAX_NAME_WORDS;
  });
  let name = words.map(camel).join('');
  if (name.length > MAX_NAME_CHARS) name = name.slice(0, MAX_NAME_CHARS);
  if (!/[A-Za-z]/.test(name)) {
    const id = String(jobId || '').replace(/[^A-Za-z0-9]/g, '').slice(0, 8);
    return `ai-widget-${id || 'generated'}.svghmi`;
  }
  return `${name}.svghmi`;
}

// ── Icon requests ───────────────────────────────────────────────────────────
// Mirror of isIconRequest in svghmi_server services/iconGenerator.js (the
// backend routes such prompts to the fast icon path). Only drives a hint under
// the prompt box — the backend decides for real — so a drift costs a wrong
// hint, never a wrong charge. Keep in step with ICON_WORD_RE / NOT_ICON_RE.
const ICON_WORD_RE = new RegExp([
  // ru
  'иконк', 'пиктограмм', 'значок', 'значк', 'для\\s+кнопк', 'на\\s+кнопк',
  // en
  '\\bicons?\\b', '\\bpictograms?\\b', '\\bglyph\\b', 'for\\s+(?:a|the)\\s+button', 'button\\s+(?:icon|symbol)',
  // es / it
  '\\bicono', '\\bpictograma', '\\bicona\\b', '\\bicone\\b', '\\bpittogramm',
  'para\\s+(?:un|el)\\s+bot[oó]n', 'per\\s+(?:un|il)\\s+pulsante',
  // de
  '\\bikone?n?\\b', '\\bpiktogramm',
  'symbol\\s+f[uü]r\\s+(?:eine?n?\\s+)?schaltfl[aä]che', 'f[uü]r\\s+(?:eine?n?\\s+)?schaltfl[aä]che',
].join('|'), 'iu');
const NOT_ICON_RE = /\bfavicon\b|\bapp\s+icon|\blogo|логотип/iu;

/** Does this prompt read as an icon / pictogram request? */
export function isIconRequest(text) {
  if (typeof text !== 'string' || !text.trim()) return false;
  if (NOT_ICON_RE.test(text)) return false;
  return ICON_WORD_RE.test(text);
}

/**
 * The live hint under the prompt box, or null before anything is typed.
 * `kind` is 'icon' when the Icon style card is picked; otherwise 'widget'
 * (the backend tells a widget from an equipment drawing by itself).
 * 'equipment' is still understood for callers that know it.
 * Times as measured on prod (2026-10): widgets p50 ~2 min / p80 ~5 min,
 * equipment drawings ~1–2 min, icons ~10 s.
 *
 * @param {string} kind 'widget' | 'equipment' | 'icon'
 * @param {string} prompt
 */
export function promptHint(kind, prompt) {
  if (!String(prompt || '').trim()) return null;
  if (kind === 'icon') return 'Icon — ready in ~10 s';
  if (isIconRequest(prompt)) return 'Looks like an icon — ready in ~10 s';
  if (kind === 'equipment') return 'Equipment drawing — usually 1–2 min';
  return 'Widget — usually 2–5 min · equipment drawings ~1–2 min';
}

// ── Waiting phrases ─────────────────────────────────────────────────────────
// Light, friendly lines rotated under the stage list while a job runs (owner,
// 2026-10-12: the wait must be obvious and a bit entertaining). Never alarming
// ("trickier than expected"…); the real progress is the stage list. At most
// one emoji, in the coffee line.
export const WAITING_PHRASES = Object.freeze([
  'Good moment to pour yourself a cup of coffee ☕',
  'Drawing the outlines…',
  'Aligning the flanges — nobody likes a leaky pipe',
  'Tip: once it is ready, you can change it by just describing the edit',
  'Teaching the pump which way to spin…',
  'Checking it opens cleanly in WinCC Unified…',
  'Calibrating the gauge needle against a very serious reference',
  'Tip: icons take only about 10 seconds',
  'Bribing the PLC with an extra scan cycle…',
  'Wiring the properties so your tags can drive it',
  'Polishing the gradients until the operators approve',
  'Asking the commissioning engineer for just one more minute…',
  'Running a quick FAT on your widget (Factory Acceptance Test, the fun kind)',
  'Setting the alarm limits: HH, H, L, LL — and coffee',
  'Almost there — tightening the last bolts',
]);

export const WAITING_PHRASE_MS = 7000;

/** The phrase to show after `elapsedMs` of waiting (rotates every ~7 s). */
export function waitingPhraseAt(elapsedMs) {
  const n = WAITING_PHRASES.length;
  const i = Math.floor(Math.max(0, Number(elapsedMs) || 0) / WAITING_PHRASE_MS) % n;
  return WAITING_PHRASES[i];
}

// ── Wait estimates ──────────────────────────────────────────────────────────
// How long a job usually takes, per kind of job, in seconds — the same shape
// as the backend's `estimate_seconds` (utils/generationEstimates.js):
//   typical    ≈ p50, "usually about …"
//   usualMax   ≈ p80, "can take up to …"; past it the job is "taking longer
//              than usual"
//   max        the hard ceiling
// The server's estimate (POST /generate, /refine, /job/:id/more, GET /job/:id,
// /active, SSE statuses) wins; these per-mode fallbacks mirror it.
export const WAIT_ESTIMATES = Object.freeze({
  widget: Object.freeze({ typical: 130, usualMax: 310, max: 600 }),
  equipment: Object.freeze({ typical: 60, usualMax: 120, max: 300 }),
  icon: Object.freeze({ typical: 8, usualMax: 15, max: 25 }),
  refine: Object.freeze({ typical: 60, usualMax: 130, max: 240 }),
  more: Object.freeze({ typical: 150, usualMax: 330, max: 600 }),
});

const positiveSeconds = (v) => (Number.isFinite(Number(v)) && Number(v) > 0 ? Number(v) : null);

/**
 * The wait estimate for a job: the server's `estimate_seconds` when it is
 * usable, else the fallback for `mode` ('widget' | 'equipment' | 'icon' |
 * 'refine' | 'more'; anything else counts as 'widget'). Never
 * typical > usualMax > max out of order.
 *
 * @param {string} mode
 * @param {{typical?: number, usual_max?: number, max?: number}|null} [serverEstimate]
 * @returns {{typical: number, usualMax: number, max: number}}
 */
export function waitEstimate(mode, serverEstimate) {
  const base = WAIT_ESTIMATES[mode] || WAIT_ESTIMATES.widget;
  const srv = serverEstimate || {};
  const typical = positiveSeconds(srv.typical) || base.typical;
  const serverMax = positiveSeconds(srv.max);
  // An older backend sent { typical, max } only: its max is then the
  // "can take up to" figure as well.
  const usualMax = Math.max(typical, positiveSeconds(srv.usual_max) || serverMax || base.usualMax);
  const max = Math.max(usualMax, serverMax || base.max);
  return { typical, usualMax, max };
}

/** "about 10 s", "about 2 min" — a rounded, human duration. */
export function formatWait(seconds) {
  const s = Math.max(1, Math.round(Number(seconds) || 0));
  if (s < 60) return `${Math.max(5, Math.round(s / 5) * 5)} s`;
  const min = Math.round(s / 60);
  return `${min} min`;
}

const usualMaxOf = (estimate) => estimate.usualMax || estimate.max || estimate.typical;

/** The expectation line under the progress: "Usually about 2 min, can take up to 5 min." */
export function waitExpectation(estimate) {
  const t = formatWait(estimate.typical);
  const m = formatWait(usualMaxOf(estimate));
  if (t === m) return `Usually about ${t}.`;
  return `Usually about ${t}, can take up to ${m}.`;
}

/** Has the job run past its usual maximum (≈ p80)? Then say so, kindly. */
export function isTakingLonger(elapsedMs, estimate) {
  const limit = estimate.usualMax || estimate.typical;
  return Number(elapsedMs) > limit * 1000;
}

/**
 * Width (0…1) of the soft time bar: elapsed against the typical time, eased so
 * it never reaches the end on its own — the bar is a feel for time, not a
 * promise. Past the usual maximum the UI switches to an indeterminate bar.
 */
export function waitBarFraction(elapsedMs, estimate) {
  const x = Math.max(0, Number(elapsedMs) || 0) / (estimate.typical * 1000);
  return Math.min(0.9, 1 - Math.exp(-1.6 * x));
}

// ── Refine tariff ───────────────────────────────────────────────────────────
/**
 * What the next edit of the lineage on screen costs. Normally the first
 * `freeRefines` edits are free, then `refinePrice` each. A lineage that starts
 * from the user's own imported file (revision kind 'import') pays its FIRST
 * edit like a generation, then gets the usual free edits (backend: refine of
 * an import, routes/ai-generator.js).
 *
 * @param {{kind: string}[]} revisions
 * @param {{freeRefines: number, refinePrice: number, generationPrice: number}} prices
 * @returns {{free: boolean, price: number, freeLeft: number, importFirst: boolean}}
 */
export function refineTariff(revisions, { freeRefines, refinePrice, generationPrice }) {
  const list = Array.isArray(revisions) ? revisions : [];
  const refines = list.filter((r) => r && r.kind === 'refine').length;
  const fromImport = !!list[0] && list[0].kind === 'import';
  if (fromImport && refines === 0) {
    return {
      free: false, price: generationPrice, freeLeft: 0, importFirst: true,
    };
  }
  const counted = fromImport ? refines - 1 : refines;
  const free = counted < freeRefines;
  return {
    free,
    price: free ? 0 : refinePrice,
    freeLeft: Math.max(0, freeRefines - counted),
    importFirst: false,
  };
}

// ── Download name ───────────────────────────────────────────────────────────
const SELF_TAG_RE = /<hmi:self\b[^>]*>/;

/**
 * The file name the server gives a widget: Content-Disposition's filename
 * (GET /download/:file — the widget's unique WinCC type name, e.g.
 * "AiWidget_Pump_3f2a1c.svghmi"); when the header is not readable (CORS does
 * not expose it), the same name read from the file's <hmi:self name="…">.
 * Null when neither has one — the caller then falls back to widgetFileName.
 *
 * @param {string|null} contentDisposition
 * @param {string} [svghmiText]
 */
export function serverWidgetFileName(contentDisposition, svghmiText) {
  const cd = String(contentDisposition || '');
  const star = /filename\*\s*=\s*(?:UTF-8'')?([^;]+)/i.exec(cd);
  const plain = /filename\s*=\s*"?([^";]+)"?/i.exec(cd);
  let name = null;
  try {
    name = star ? decodeURIComponent(star[1].trim().replace(/^"|"$/g, '')) : null;
  } catch { name = null; }
  if (!name && plain) name = plain[1].trim();
  if (name) {
    const safe = name.replace(/^.*[\\/]/, '').replace(/[^A-Za-z0-9_.-]/g, '');
    if (/^[A-Za-z0-9_-][A-Za-z0-9_.-]*\.svghmi$/i.test(safe)) return safe;
  }
  const tag = SELF_TAG_RE.exec(String(svghmiText || ''));
  const m = tag && /\sname\s*=\s*"([^"]*)"/.exec(tag[0]);
  const bare = m ? m[1].replace(/^extended\./, '').replace(/[^A-Za-z0-9_-]/g, '') : '';
  return bare ? `${bare}.svghmi` : null;
}

// ── Style pictures and the showcase ─────────────────────────────────────────
// Example pictures live in public/ (generated by the owner with the
// generator itself); every consumer falls back gracefully when one is missing.
export const STYLE_THUMB_PX = 132;
const STYLE_IMG_DIR = '/images/ai-generator/styles';
const SHOWCASE_DIR = '/images/ai-generator/showcase';
export const SHOWCASE_MANIFEST_URL = `${SHOWCASE_DIR}/showcase.json`;
export const SHOWCASE_HERO_SRC = `${SHOWCASE_DIR}/hero-hmi-screen.webp`;
const SAFE_FILE_RE = /^[\w.-]+\.(?:png|jpe?g|webp|svg)$/i;
const SAFE_ID_RE = /^[\w-]+$/;
const SHOWCASE_MAX = 12;
const SHOWCASE_KINDS = new Set(['widget', 'equipment', 'icon']);
const SHOWCASE_TITLE_MAX = 40;
// Short label of a tile's style (the style card names are longer).
const SHOWCASE_STYLE_LABELS = Object.freeze({
  flat: 'Flat',
  convex: 'Convex',
  'hp-hmi': 'HP-HMI',
  'line-art': 'Line-art',
  dark: 'Dark',
  icon: 'Icon',
});

/** Picture of a style card: /images/ai-generator/styles/<id>.webp. */
export function styleThumbSrc(id) {
  const safe = SAFE_ID_RE.test(String(id || '')) ? id : 'auto';
  return `${STYLE_IMG_DIR}/${safe}.webp`;
}

/**
 * Showcase manifest → tiles. Keeps only well-formed entries ({ file, prompt,
 * title?, style?, detail?, kind? } with a plain file name), at most 12, each
 * with its `src`, a short `title` (≤ 40 chars; the prompt's first clause when
 * absent) and `styleLabel`; `kind` is widget | equipment | icon (anything
 * else → widget). The full prompt is what a click puts into the prompt box.
 * Anything else (missing file, HTML 404 page, wrong shape) → [].
 *
 * @param {unknown} json
 * @returns {{file: string, src: string, prompt: string, title: string,
 *   style: string|null, styleLabel: string|null, detail: string|null, kind: string}[]}
 */
export function parseShowcaseManifest(json) {
  let list = [];
  if (Array.isArray(json)) list = json;
  else if (json && Array.isArray(json.items)) list = json.items;
  const str = (v) => (typeof v === 'string' && v.trim() ? v.trim() : null);
  return list
    .filter((it) => it && SAFE_FILE_RE.test(String(it.file || '')) && str(it.prompt))
    .slice(0, SHOWCASE_MAX)
    .map((it) => ({
      file: it.file,
      src: `${SHOWCASE_DIR}/${it.file}`,
      prompt: str(it.prompt).slice(0, 1000),
      // A short tile title; without one, the prompt's first clause.
      title: (str(it.title) || str(it.prompt).split(/[,.;(]/)[0]).slice(0, SHOWCASE_TITLE_MAX),
      style: str(it.style),
      styleLabel: SHOWCASE_STYLE_LABELS[str(it.style)] || null,
      detail: str(it.detail),
      kind: SHOWCASE_KINDS.has(it.kind) ? it.kind : 'widget',
    }));
}

// ── History lineages ────────────────────────────────────────────────────────
/**
 * Fold a job history (GET /my-jobs, newest first) into one card per widget:
 * an edit (refine/restyle) is shown under its original instead of as a
 * separate "widget" titled by the edit instruction. Needs `root_job_id` on the
 * jobs; without it every job stays its own card (older backend).
 *
 * @param {object[]} jobs
 * @returns {{ job: object, title: string, edits: number }[]} newest first;
 *   `job` is the newest finished revision of the lineage (else the newest one)
 */
export function groupJobLineages(jobs) {
  const list = Array.isArray(jobs) ? jobs.filter((j) => j && j.job_id) : [];
  const byId = new Map(list.map((j) => [j.job_id, j]));
  const groups = new Map();
  list.forEach((j) => {
    const root = j.root_job_id && j.root_job_id !== j.job_id ? j.root_job_id : j.job_id;
    if (!groups.has(root)) groups.set(root, []);
    groups.get(root).push(j);
  });
  return [...groups.entries()].map(([rootId, members]) => {
    const finished = members.find((m) => m.status === 'completed');
    const job = finished || members[0];
    const rootJob = byId.get(rootId);
    const edits = members.filter((m) => m.job_id !== rootId).length;
    let title = (rootJob && rootJob.prompt) || job.prompt || '';
    if (!rootJob && job.job_id !== rootId) title = `Edited widget — ${job.prompt || ''}`;
    return { job, title, edits };
  });
}
